import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";

import SideBarMenu from "../Component/SideBarMenu";
import TopHeader from "../Component/TopHeader";
import ConfirmModal from "../Component/Models/ConfirmModal";

import { showToast } from "../../utils/helper";
import {
  getAppVersionAction,
  updateAppVersionAction,
} from "../../Store/Actions/AdminActions";
import { logoutAction } from "../../Store/Actions/AuthActions";

// Mirrors the server validator: ^\d+(\.\d+){0,3}$ (e.g. "2.5", "2.10.0").
const VERSION_REGEX = /^\d+(\.\d+){0,3}$/;
const isValidVersion = (v) => typeof v === "string" && VERSION_REGEX.test(v.trim());

const isValidUrl = (u) => {
  if (!u) return true;
  const trimmed = String(u).trim();
  return /^https?:\/\//i.test(trimmed);
};

const EMPTY_FORM = {
  iosMinVersion: "",
  iosLatestVersion: "",
  iosStoreUrl: "",
  androidMinVersion: "",
  androidLatestVersion: "",
  androidStoreUrl: "",
  message: "",
};

// Map the server's GET shape into the flat form shape we render.
const toFormShape = (data) => ({
  iosMinVersion: (data && data.ios && data.ios.minVersion) || "",
  iosLatestVersion: (data && data.ios && data.ios.latestVersion) || "",
  iosStoreUrl: (data && data.ios && data.ios.storeUrl) || "",
  androidMinVersion: (data && data.android && data.android.minVersion) || "",
  androidLatestVersion: (data && data.android && data.android.latestVersion) || "",
  androidStoreUrl: (data && data.android && data.android.storeUrl) || "",
  message: (data && data.message) || "",
});

// Compare numeric-dotted versions: returns positive if a > b, 0 if equal,
// negative if a < b. Non-comparable inputs return 0 so we don't false-alarm.
const compareVersions = (a, b) => {
  if (!isValidVersion(a) || !isValidVersion(b)) return 0;
  const pa = a.trim().split(".").map((n) => parseInt(n, 10));
  const pb = b.trim().split(".").map((n) => parseInt(n, 10));
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x - y;
  }
  return 0;
};

const AppVersionSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const menuState = useSelector((state) => state.auth.isMenuOpen);

  // Loaded server state (for diffing + cancel/reset).
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirm modal for raising a minVersion (lockout risk).
  const [confirmModal, setConfirmModal] = useState({ show: false, payload: null });

  const handle401 = () => {
    showToast("error", "Your admin session has expired. Please log in again.");
    logoutAction(dispatch);
    navigate("/");
  };

  const loadConfig = () => {
    setLoading(true);
    setLoadError(false);
    getAppVersionAction()
      .then((res) => {
        if (res && res.status === 401) {
          handle401();
          return;
        }
        if (res && res.type === "success" && res.data) {
          const next = toFormShape(res.data);
          setLoaded(next);
          setForm(next);
          setFieldErrors({});
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  // Build the partial payload — only fields the admin actually changed,
  // with versions/URLs trimmed. message: "" must be sent explicitly to clear,
  // so we treat it like any other diff.
  const buildPayload = (scope) => {
    if (!loaded) return {};
    const fields = (() => {
      if (scope === "ios") {
        return ["iosMinVersion", "iosLatestVersion", "iosStoreUrl"];
      }
      if (scope === "android") {
        return ["androidMinVersion", "androidLatestVersion", "androidStoreUrl"];
      }
      if (scope === "message") {
        return ["message"];
      }
      return Object.keys(EMPTY_FORM);
    })();

    const payload = {};
    fields.forEach((k) => {
      const current = (form[k] || "").trim();
      const original = (loaded[k] || "").trim();
      if (current !== original) {
        payload[k] = current;
      }
    });
    return payload;
  };

  // Pre-flight client validation, scoped to the fields we're about to send.
  const validatePayload = (payload) => {
    const errs = {};
    Object.keys(payload).forEach((k) => {
      const v = (payload[k] || "").trim();
      if (k.endsWith("MinVersion") || k.endsWith("LatestVersion")) {
        if (v !== "" && !isValidVersion(v)) {
          errs[k] = 'Use dotted-numeric like "1.2.0".';
        }
      }
      if (k.endsWith("StoreUrl")) {
        if (v !== "" && !isValidUrl(v)) {
          errs[k] = "Must start with http:// or https://, or be empty.";
        }
      }
      if (k === "message" && v.length > 1000) {
        errs.message = "Message must be 1000 characters or less.";
      }
    });
    return errs;
  };

  // Returns the platforms whose minVersion would be raised by this payload.
  const minBumps = (payload) => {
    const bumps = [];
    if (
      payload.iosMinVersion !== undefined &&
      compareVersions(payload.iosMinVersion, loaded.iosMinVersion) > 0
    ) {
      bumps.push({
        label: "iOS",
        from: loaded.iosMinVersion || "—",
        to: payload.iosMinVersion,
      });
    }
    if (
      payload.androidMinVersion !== undefined &&
      compareVersions(payload.androidMinVersion, loaded.androidMinVersion) > 0
    ) {
      bumps.push({
        label: "Android",
        from: loaded.androidMinVersion || "—",
        to: payload.androidMinVersion,
      });
    }
    return bumps;
  };

  const doSave = (payload) => {
    setSaving(true);
    setFieldErrors({});
    updateAppVersionAction(payload)
      .then((res) => {
        if (res && res.status === 401) {
          handle401();
          return;
        }
        if (res && res.type === "success" && res.data) {
          const next = toFormShape(res.data);
          setLoaded(next);
          setForm(next);
          showToast("success", res.message || "Version info updated");
          setConfirmModal({ show: false, payload: null });
        } else if (res && Array.isArray(res.details) && res.details.length > 0) {
          const map = {};
          res.details.forEach((d) => {
            if (d && d.field) map[d.field] = d.message || "Invalid value.";
          });
          setFieldErrors(map);
          showToast("error", "Please fix the highlighted fields.");
        } else if (res && res.message) {
          showToast("error", res.message);
        } else {
          showToast("error", "Could not save changes.");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not save changes."),
      )
      .finally(() => setSaving(false));
  };

  // Save flow: build payload, validate, warn on minVersion bumps, then save.
  const handleSave = (scope) => {
    if (saving || !loaded) return;
    const payload = buildPayload(scope);

    if (Object.keys(payload).length === 0) {
      showToast("error", "No changes to save.");
      return;
    }

    const errs = validatePayload(payload);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      showToast("error", "Please fix the highlighted fields.");
      return;
    }

    const bumps = minBumps(payload);
    if (bumps.length > 0) {
      setConfirmModal({ show: true, payload, bumps });
      return;
    }

    doSave(payload);
  };

  const handleReset = () => {
    if (!loaded) return;
    setForm(loaded);
    setFieldErrors({});
  };

  // Visible warning when the admin is currently typing a min bump (before save).
  const liveBumps = useMemo(() => {
    if (!loaded) return [];
    const draft = buildPayload("all");
    return minBumps(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loaded]);

  const renderField = (key, label, placeholder, opts = {}) => (
    <Form.Group className="mb-3" controlId={`app-version-${key}`}>
      <Form.Label className="fw-bold">{label}</Form.Label>
      <Form.Control
        type={opts.textarea ? undefined : "text"}
        as={opts.textarea ? "textarea" : undefined}
        rows={opts.textarea ? 3 : undefined}
        placeholder={placeholder}
        value={form[key]}
        onChange={(e) => onChange(key, e.target.value)}
        isInvalid={!!fieldErrors[key]}
        maxLength={opts.textarea ? 1000 : undefined}
      />
      {fieldErrors[key] && (
        <Form.Control.Feedback type="invalid">
          {fieldErrors[key]}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );

  return (
    <div className={`app-wrapper ${menuState ? "open" : ""}`}>
      <SideBarMenu />

      <div className="content-wrapper">
        <TopHeader title="App Versions" />

        <div className="content">
          {loading && (
            <div className="text-center my-5">
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} />
              <p className="text-muted mt-2 mb-0">Loading current config…</p>
            </div>
          )}

          {!loading && loadError && (
            <div className="appv-load-error">
              <p className="mb-3">
                We couldn't load the current version config. Please refresh.
              </p>
              <Button
                variant=""
                className="btn-pill-primary"
                onClick={loadConfig}
              >
                <span>
                  <i className="fas fa-redo me-2" />
                  Try again
                </span>
              </Button>
            </div>
          )}

          {!loading && !loadError && loaded && (
            <>
              {liveBumps.length > 0 && (
                <div className="appv-warning">
                  <i className="fas fa-exclamation-triangle me-2" />
                  <div>
                    <strong>Heads up:</strong> raising the minimum version will
                    block every user on a lower build from using the app. Only
                    save this after your new build is live in the{" "}
                    {liveBumps.map((b) => b.label).join(" / ")} store.
                    <ul className="mb-0 mt-2">
                      {liveBumps.map((b) => (
                        <li key={b.label}>
                          {b.label} minimum {b.from} → <strong>{b.to}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="row appv-columns">
                <div className="col-md-6">
                  <div className="appv-card">
                    <div className="appv-card-head">
                      <i className="fab fa-apple" />
                      <h4>iOS</h4>
                    </div>
                    <Form onSubmit={(e) => e.preventDefault()}>
                      {renderField(
                        "iosMinVersion",
                        "Minimum version",
                        "e.g. 2.5",
                      )}
                      {renderField(
                        "iosLatestVersion",
                        "Latest version",
                        "e.g. 2.6",
                      )}
                      {renderField(
                        "iosStoreUrl",
                        "App Store URL",
                        "https://apps.apple.com/…",
                      )}
                      <div className="appv-actions">
                        <Button
                          type="button"
                          variant=""
                          className="btn-pill-primary"
                          disabled={saving}
                          onClick={() => handleSave("ios")}
                        >
                          <span>
                            {saving && (
                              <i className="fas fa-spin fa-spinner me-2" />
                            )}
                            Save iOS
                          </span>
                        </Button>
                      </div>
                    </Form>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="appv-card">
                    <div className="appv-card-head">
                      <i className="fab fa-android" />
                      <h4>Android</h4>
                    </div>
                    <Form onSubmit={(e) => e.preventDefault()}>
                      {renderField(
                        "androidMinVersion",
                        "Minimum version",
                        "e.g. 1.1.8",
                      )}
                      {renderField(
                        "androidLatestVersion",
                        "Latest version",
                        "e.g. 1.2.0",
                      )}
                      {renderField(
                        "androidStoreUrl",
                        "Play Store URL",
                        "https://play.google.com/…",
                      )}
                      <div className="appv-actions">
                        <Button
                          type="button"
                          variant=""
                          className="btn-pill-primary"
                          disabled={saving}
                          onClick={() => handleSave("android")}
                        >
                          <span>
                            {saving && (
                              <i className="fas fa-spin fa-spinner me-2" />
                            )}
                            Save Android
                          </span>
                        </Button>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>

              <div className="appv-card mt-3">
                <div className="appv-card-head">
                  <i className="far fa-comment-alt" />
                  <h4>Update modal message (optional)</h4>
                </div>
                <Form onSubmit={(e) => e.preventDefault()}>
                  {renderField(
                    "message",
                    "Message",
                    "Leave empty for default copy",
                    { textarea: true },
                  )}
                  <p className="text-muted small mb-3">
                    Shown inside the update prompt. Up to 1000 characters.
                    Empty = mobile app uses its default copy.
                  </p>
                  <div className="appv-actions">
                    <Button
                      type="button"
                      variant=""
                      className="btn-pill-primary"
                      disabled={saving}
                      onClick={() => handleSave("message")}
                    >
                      <span>
                        {saving && (
                          <i className="fas fa-spin fa-spinner me-2" />
                        )}
                        Save message
                      </span>
                    </Button>
                    <Button
                      type="button"
                      variant=""
                      className="btn-pill-outline ms-2"
                      disabled={saving}
                      onClick={handleReset}
                    >
                      <span>Reset</span>
                    </Button>
                  </div>
                </Form>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        show={confirmModal.show}
        title="Raise minimum version?"
        message={
          confirmModal.bumps
            ? `You're about to raise the minimum required version for ${confirmModal.bumps
                .map((b) => `${b.label} (${b.from} → ${b.to})`)
                .join(
                  " and ",
                )}. Every user on a lower build will be locked out until they update. Make sure the new build is already live in the store.`
            : "Are you sure?"
        }
        confirmLabel="Yes, raise version"
        cancelLabel="Cancel"
        busy={saving}
        onClose={() => setConfirmModal({ show: false, payload: null })}
        onConfirm={() => doSave(confirmModal.payload)}
      />
    </div>
  );
};

export default AppVersionSettings;
